import React, { FC, useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  GetSalesInboxResponse,
  SalesInboxConversation,
  GetSalesInboxCampaignsResponse,
  SalesInboxCampaign,
} from '@dreamai/types';
import api from '../../../api';
import SalesInboxList from './SalesInboxList';
import SalesInboxConversationView from './SalesInboxConversationView';
import {
  conversationNeedsAttention,
  conversationIsPositive,
  conversationIsNegative,
  getFilterCounts,
} from './salesInboxHelpers';
import { OnboardingModal } from '../../onboarding/OnboardingModal';
import { ReactComponent as ChevronDownIcon } from '../../../icons/chevron-down.svg';
import './sales-inbox.scss';

// Encode/decode conversation ID for URL obscuring
const encodeConversationId = (id: number): string => {
  return btoa(`conv-${id}-${Date.now() % 1000}`);
};

const decodeConversationId = (encoded: string): number | null => {
  try {
    const decoded = atob(encoded);
    const match = decoded.match(/^conv-(\d+)-/);
    return match ? Number(match[1]) : null;
  } catch {
    return null;
  }
};

const SalesInbox: FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [conversations, setConversations] = useState<SalesInboxConversation[]>(
    [],
  );
  const [campaigns, setCampaigns] = useState<SalesInboxCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(
    null,
  );
  const [selectedReplyType, setSelectedReplyType] =
    useState<string>('needs_attention');

  // Selected conversation - initialize from URL params (decode if present)
  const conversationParam = searchParams.get('c');
  const [selectedConversationId, setSelectedConversationId] = useState<
    number | null
  >(conversationParam ? decodeConversationId(conversationParam) : null);

  // Fetch campaigns for filter dropdown
  const fetchCampaigns = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await api.get<GetSalesInboxCampaignsResponse>(
        '/sales-inbox/campaigns',
      );

      if (response.data.success) {
        setCampaigns(response.data.campaigns);
      }
    } catch (err) {
      console.error('Error fetching campaigns:', err);
    }
  }, [getAccessTokenSilently]);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const params: Record<string, any> = {
        limit: 50,
      };

      if (selectedCampaignId) {
        params.campaignId = selectedCampaignId;
      }

      const response = await api.get<GetSalesInboxResponse>(
        '/sales-inbox/conversations',
        { params },
      );

      if (response.data.success) {
        setConversations(response.data.conversations);
      }
    } catch (err: any) {
      console.error('Error fetching conversations:', err);
      setError(err.message || 'Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, selectedCampaignId]);

  // Initial load
  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Handle conversation selection
  const handleSelectConversation = (conversationId: number) => {
    setSelectedConversationId(conversationId);
    setSearchParams({ c: encodeConversationId(conversationId) });
  };

  // Handle back to list
  const handleBackToList = () => {
    setSelectedConversationId(null);
    setSearchParams({});
    // Refresh conversations when returning
    fetchConversations();
  };

  // Handle marking conversation as handled (dismiss awaiting reply / may want to respond)
  const handleMarkConversationHandled = useCallback(
    async (conversationId: number) => {
      // Optimistic update: immediately update the UI
      setConversations((prevConversations) =>
        prevConversations.map((c) =>
          c.id === conversationId
            ? { ...c, awaitingOurResponse: false, mayWantToRespond: false }
            : c,
        ),
      );

      try {
        const token = await getAccessTokenSilently();
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        await api.post(
          `/campaigns/conversations/${conversationId}/mark-handled`,
        );

        // Reload conversations to ensure consistency with backend
        fetchConversations();
      } catch (error) {
        console.error('Error marking conversation as handled:', error);
        // Revert optimistic update on error
        fetchConversations();
      }
    },
    [getAccessTokenSilently, fetchConversations],
  );

  // Filter out nulls first
  const validConversations = conversations.filter(
    (c): c is SalesInboxConversation => c !== null,
  );

  // Get counts for each filter type
  const filterCounts = getFilterCounts(validConversations);

  // Apply reply type filter
  const filteredConversations = validConversations.filter((c) => {
    if (selectedReplyType === 'all') return true;
    if (selectedReplyType === 'needs_attention') {
      return conversationNeedsAttention(c);
    }
    if (selectedReplyType === 'positive') {
      return conversationIsPositive(c);
    }
    if (selectedReplyType === 'negative') {
      return conversationIsNegative(c);
    }
    return true;
  });

  // Get selected conversation details
  const selectedConversation = filteredConversations.find(
    (c) => c.id === selectedConversationId,
  );

  // Render conversation view if one is selected
  if (selectedConversationId && selectedConversation) {
    return (
      <SalesInboxConversationView
        conversationId={selectedConversationId}
        prospect={{
          id: selectedConversation.prospectId,
          name: selectedConversation.prospectName,
          email: selectedConversation.prospectEmail,
          orgName: selectedConversation.orgName,
        }}
        campaignName={selectedConversation.campaignName}
        onBack={handleBackToList}
        onReplyCreated={fetchConversations}
      />
    );
  }

  // Render list view
  return (
    <div className='sales-inbox inset-white-backed'>
      <div className='sales-inbox-header'>
        <h1>Sales Inbox</h1>
        <div className='filters'>
          <label className='filter-label'>Filter by</label>
          <div className='filter-select-wrapper'>
            <select
              value={selectedCampaignId || ''}
              onChange={(e) =>
                setSelectedCampaignId(
                  e.target.value ? Number(e.target.value) : null,
                )
              }
              className='filter-select'
            >
              <option value=''>All Campaigns</option>
              {campaigns.map((campaign) => (
                <option key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </option>
              ))}
            </select>
            <ChevronDownIcon />
          </div>
          <div className='filter-select-wrapper'>
            <select
              value={selectedReplyType}
              onChange={(e) => setSelectedReplyType(e.target.value)}
              className='filter-select'
            >
              <option value='needs_attention'>
                Needs Attention ({filterCounts.needsAttention})
              </option>
              <option value='positive'>
                Positive Replies ({filterCounts.positive})
              </option>
              <option value='negative'>
                Negative Replies ({filterCounts.negative})
              </option>
              <option value='all'>All Replies ({filterCounts.all})</option>
            </select>
            <ChevronDownIcon />
          </div>
        </div>
      </div>

      <SalesInboxList
        conversations={filteredConversations}
        loading={loading}
        error={error}
        onSelectConversation={handleSelectConversation}
        onMarkConversationHandled={handleMarkConversationHandled}
      />

      <OnboardingModal
        modalKey='sales_inbox_intro'
        title='Your new Sales Inbox'
        imageSrc='/onboarding-modals/sales-inbox-intro.png'
        primaryButtonText='Get started'
      >
        <p>
          All your communication with customers is now in one place. You can
          find your replies with filters by campaign or email type.
        </p>
        <p>
          Your Sales Manager will automatically create new replies for you to
          approve, or let you know if they can't respond to a customer question.
        </p>
        <p>
          For any email you can reply or forward it yourself, putting you in
          control of your conversations.
        </p>
      </OnboardingModal>
    </div>
  );
};

export default SalesInbox;
